import React, { useEffect, useState } from 'react';
import { Container, Box, Card, CardContent, Typography, Dialog, DialogActions, DialogContent, DialogTitle, Button, FormControl, InputLabel, Select, MenuItem, CircularProgress } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { styled } from '@mui/system';
import AddIcon from '@mui/icons-material/Add';
import { useDispatch, useSelector } from 'react-redux';
import { getAllPeriods, addPeriod } from '../../api/handlePeriods'
import { downloadPasswordResetLinks } from '../../api/auth'
import MySnackbar from '../../components/UI/MySnackBar';
import { setPeriod } from '../../redux/slices/periodSlice';
import { AddCardStyled } from '../../styles/AddCardStyled';
import { HomeViewTitle } from '../../styles/Titles';
import { RootCeleste } from '../../components/Root';
const Root = RootCeleste;
const CardContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  justifyContent: 'center',
  gap: theme.spacing(3),
  marginTop: theme.spacing(4),
}));

const CardStyled = styled(Card)(({ theme }) => ({
  width: '200px',
  height: '150px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  textAlign: 'center',
  borderRadius: theme.shape.borderRadius,
  boxShadow: theme.shadows[3],
  backgroundColor: '#FFFFFF',
  cursor: 'pointer',
  transition: 'transform 0.3s',
  '&:hover': {
    transform: 'scale(1.05)',
  },
}));

const Title = HomeViewTitle;

// El rango arranca en 2020 y no en el anio actual - 1: hay cuatrimestres historicos
// que se cargan despues (y periodos viejos que se usan para probar), y el backend
// acepta cualquier 20xx (REGEX_PERIOD). Antes solo ofrecia [anio-1 .. anio+2], asi que
// un cuatrimestre anterior no se podia crear desde la UI.
const FIRST_YEAR = 2020;
const years = Array.from(
  { length: new Date().getFullYear() + 2 - FIRST_YEAR + 1 },
  (_, i) => new Date().getFullYear() + 2 - i
);
const terms = ['1', '2'];

const AdminHomeView = () => {
  const user = useSelector((state) => state.user);
  const [periods, setPeriods] = useState([]);
  const [open, setOpen] = useState(false);
  const [newPeriod, setNewPeriod] = useState({ year: '', term: '' });
  const navigate = useNavigate();
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const [notification, setNotification] = useState({
    open: false,
    message: "",
    status: "",
  });

  const handleSnackbarClose = () => {
    setNotification({ ...notification, open: false });
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        const data = await getAllPeriods(user);
        const sortedData = data.sort((a, b) => {
          const [termA, yearA] = [parseInt(a.id[0]), parseInt(a.id.slice(2))];
          const [termB, yearB] = [parseInt(b.id[0]), parseInt(b.id.slice(2))];
          
          if (yearA === yearB) {
            return termA - termB; // Si el año es igual, compara por cuatrimestre
          }
          return yearA - yearB; // Si el año es diferente, ordena por año
        });        
        setPeriods(sortedData);
      } catch (error) {
        const errorMessage = "Error al obtener los cuatrimestres: " + error.message;
        setError(errorMessage);
        setNotification({
          open: true,
          message: errorMessage,
          status: "error",
        });
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleAddPeriod = async () => {
    if (newPeriod.year && newPeriod.term) {
      const newEntry = `${newPeriod.term}C${newPeriod.year}`;
      try {
        const newPeriod = await addPeriod(newEntry, user); // Call the add function
        setPeriods([...periods, newPeriod]);
        handleClose();
        setNotification({
          open: true,
          message: "Cuatrimestre creado exitosamente!",
          status: "success",
        });
      } catch (error) {
        setNotification({
          open: true,
          message: "Cuatrimestre ya existente",
          status: "error",
        });
      }
    }
  };
  const dispatch = useDispatch();

  const downloadResetLinks = async () => {
    try {
      await downloadPasswordResetLinks(user);
      setNotification({
        open: true,
        message: "Links de reset descargados exitosamente",
        status: "success",
      });
    } catch (error) {
      setNotification({
        open: true,
        message: "Error al descargar los links de reset: " + error.message,
        status: "error",
      });
    }
  };

  const handleCardClick = (period) => {
    dispatch(setPeriod(period))
    navigate(`/dashboard/${period.id}`);
  };

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setNewPeriod({ year: '', term: '' });
  };

  if (error) {
    return <Root maxWidth="md">
      <Title variant="h4">¡{user.name}, te damos la bienvenida!</Title>
      <Typography color="error" variant="body1" fontWeight="bold">{error}</Typography>
      <MySnackbar
        open={notification.open}
        handleClose={handleSnackbarClose}
        message={notification.message}
        status={notification.status}
      />
    </Root>
  }

  return (
    <Root maxWidth="md">
      <Title variant="h4">¡{user.name}, te damos la bienvenida!</Title>
      <Typography variant="h5" style={{ color: '#555' }}>Cuatrimestres</Typography>
      {isLoading ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
          <CircularProgress />
        </Box>
      ) : (
        <CardContainer>
          {periods.map((period, index) => (
            <CardStyled key={index} onClick={() => handleCardClick(period)}>
              <CardContent>
                <Typography variant="h6" style={{ color: '#333' }}>{period.id}</Typography>
              </CardContent>
            </CardStyled>
          ))}
          <AddCardStyled onClick={handleClickOpen}>
            <CardContent>
              <AddIcon style={{ fontSize: '2rem', color: '#888' }} />
            </CardContent>
          </AddCardStyled>
        </CardContainer>
      )}
      <Dialog open={open} onClose={handleClose}>
        <DialogTitle>Agregar Nuevo Cuatrimestre</DialogTitle>
        <DialogContent>
          <FormControl variant="outlined" fullWidth margin="normal">
            <InputLabel>Año</InputLabel>
            <Select
              value={newPeriod.year}
              onChange={(e) => setNewPeriod({ ...newPeriod, year: e.target.value })}
              label="Año"
            >
              {years.map((year) => (
                <MenuItem key={year} value={year}>
                  {year}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl variant="outlined" fullWidth margin="normal">
            <InputLabel>Cuatrimestre</InputLabel>
            <Select
              value={newPeriod.term}
              onChange={(e) => setNewPeriod({ ...newPeriod, term: e.target.value })}
              label="Cuatrimestre"
            >
              {terms.map((term) => (
                <MenuItem key={term} value={term}>
                  {term}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="error" variant="outlined">
            Cancelar
          </Button>
          <Button onClick={handleAddPeriod} color="primary" variant="contained">
            Agregar
          </Button>
        </DialogActions>
      </Dialog>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, justifyContent: 'center', marginTop: 3 }}>
        <Button onClick={() => navigate('/admins')} color="primary" variant="contained">Administradores</Button>
        <Button onClick={() => navigate('/directores')} color="primary" variant="contained">Directores</Button>
        <Button onClick={() => navigate('/plantilla-aviso-revisor')} color="primary" variant="contained">Plantilla del aviso de revisor</Button>
        <Button onClick={() => navigate('/plantilla-aviso-entrega-final')} color="primary" variant="contained">Plantilla del aviso de entrega final</Button>
        <Button onClick={() => downloadResetLinks()} color="primary" variant="contained">Bajar Reset Links de Passwords</Button>
      </Box>
      <MySnackbar
        open={notification.open}
        handleClose={handleSnackbarClose}
        message={notification.message}
        status={notification.status}
      />
    </Root>
  );
};

export default AdminHomeView;